import { useEffect, useMemo, useState } from "react";
import {
  CheckCircleIcon,
  CheckIcon,
  CopyIcon,
  FileBinaryIcon,
  FileIcon,
  GraphIcon,
  KeyIcon,
  LinkIcon,
  LockIcon,
  PlusIcon,
} from "@primer/octicons-react";
import type { ComponentType } from "react";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";

type EndpointKey =
  | "generate-pdf"
  | "public-url"
  | "list-templates"
  | "get-template"
  | "create-template"
  | "account-overview"
  | "connect-mcp";
type ResponseState = "success" | "error";
type HttpMethod = "GET" | "POST";
type McpClientKey = "generic" | "codex" | "claude" | "cursor" | "vscode" | "antigravity";

interface FieldDefinition {
  name: string;
  type: string;
  description: string;
  required?: boolean;
}

interface EndpointDefinition {
  key: EndpointKey;
  navigationLabel: string;
  group: "Generation" | "Operation" | "MCP";
  icon: ComponentType<{ size?: number }>;
  method: HttpMethod;
  path: string;
  title: string;
  description: string;
  result: string;
  fieldsLabel?: string;
  fields?: FieldDefinition[];
  requestExample: string;
  requestLanguage: string;
  requestLabel?: string;
  successResponse: string;
  errorResponse: string;
  responseLanguage?: string;
  contentTypeRequired?: boolean;
}

interface McpClientConfiguration {
  key: McpClientKey;
  label: string;
  title: string;
  description: string;
  location: string;
  language: string;
  code: string;
}

const BASE_URL = "https://app.forgepdf.io";
const MCP_URL = `${BASE_URL}/mcp`;
const templateDataExample = `{
    "company_name": "Acme Corp",
    "invoice_number": "INV-001",
    "date": "2026-07-10"
  }`;

const mcpClientConfigurations: Record<McpClientKey, McpClientConfiguration> = {
  generic: {
    key: "generic",
    label: "Generic",
    title: "Generic Streamable HTTP configuration",
    description: "Use this with clients that accept an mcpServers JSON object or ask for a remote HTTP MCP server.",
    location: "Add MCP Server / MCP settings",
    language: "json",
    code: `{
  "mcpServers": {
    "forgepdf": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_FORGEPDF_API_KEY"
      }
    }
  }
}`,
  },
  codex: {
    key: "codex",
    label: "Codex",
    title: "Codex configuration",
    description: "Add this server entry to your Codex configuration. It uses a static Bearer header, so no environment variable is required.",
    location: "~/.codex/config.toml",
    language: "toml",
    code: `[mcp_servers.forgepdf]
url = "${MCP_URL}"
http_headers = { Authorization = "Bearer YOUR_FORGEPDF_API_KEY" }`,
  },
  claude: {
    key: "claude",
    label: "Claude Code",
    title: "Claude Code command",
    description: "Run this command once in Claude Code to add ForgePDF as a remote HTTP MCP server.",
    location: "Terminal",
    language: "bash",
    code: `claude mcp add --transport http forgepdf "${MCP_URL}" \\
  --header "Authorization: Bearer YOUR_FORGEPDF_API_KEY"`,
  },
  cursor: {
    key: "cursor",
    label: "Cursor",
    title: "Cursor configuration",
    description: "Paste this into Cursor's global or project MCP configuration, then restart or reconnect the server.",
    location: "~/.cursor/mcp.json or .cursor/mcp.json",
    language: "json",
    code: `{
  "mcpServers": {
    "forgepdf": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_FORGEPDF_API_KEY"
      }
    }
  }
}`,
  },
  vscode: {
    key: "vscode",
    label: "VS Code",
    title: "Visual Studio Code configuration",
    description: "Open MCP: Open User Configuration or .vscode/mcp.json, paste this, then enter your API key in the secure prompt.",
    location: "mcp.json",
    language: "json",
    code: `{
  "inputs": [
    {
      "type": "promptString",
      "id": "forgepdf-api-key",
      "description": "ForgePDF API key",
      "password": true
    }
  ],
  "servers": {
    "forgepdf": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer \${input:forgepdf-api-key}"
      }
    }
  }
}`,
  },
  antigravity: {
    key: "antigravity",
    label: "Antigravity",
    title: "Google Antigravity configuration",
    description: "Paste this into Antigravity's global or workspace MCP configuration and refresh MCP servers.",
    location: "~/.gemini/config/mcp_config.json or .agents/mcp_config.json",
    language: "json",
    code: `{
  "mcpServers": {
    "forgepdf": {
      "serverUrl": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_FORGEPDF_API_KEY"
      }
    }
  }
}`,
  },
};

const endpoints: Record<EndpointKey, EndpointDefinition> = {
  "generate-pdf": {
    key: "generate-pdf",
    navigationLabel: "Generate PDF file",
    group: "Generation",
    icon: FileBinaryIcon,
    method: "POST",
    path: "/api/pdf",
    title: "Generate a PDF file",
    description: "Generate a document and receive the PDF binary directly in the response.",
    result: "Use this when your application needs to download, attach, or store the PDF file itself.",
    contentTypeRequired: true,
    fields: [
      { name: "template_id", type: "string", required: true, description: "The ID of the ForgePDF template to render." },
      { name: "data", type: "object", required: true, description: "The values used to populate variables in the template." },
    ],
    requestExample: `curl -X POST \\
  '${BASE_URL}/api/pdf' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "template_id": "your_template_id",
    "data": ${templateDataExample}
  }'`,
    requestLanguage: "bash",
    successResponse: `# 200 OK
# The response body contains the PDF binary.
Content-Type: application/pdf
Content-Disposition: attachment; filename=document.pdf`,
    errorResponse: `# 401 Unauthorized
Unauthorized: Api key is invalid

# 404 Not Found
Template not found`,
    responseLanguage: "http",
  },
  "public-url": {
    key: "public-url",
    navigationLabel: "Get public PDF URL",
    group: "Generation",
    icon: LinkIcon,
    method: "POST",
    path: "/api/url",
    title: "Generate a hosted PDF URL",
    description: "Generate a document and receive a hosted URL for the PDF.",
    result: "Use this when you need a shareable link without handling the PDF binary yourself.",
    contentTypeRequired: true,
    fields: [
      { name: "template_id", type: "string", required: true, description: "The ID of the ForgePDF template to render." },
      { name: "data", type: "object", required: true, description: "The values used to populate variables in the template." },
    ],
    requestExample: `curl -X POST \\
  '${BASE_URL}/api/url' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "template_id": "your_template_id",
    "data": ${templateDataExample}
  }'`,
    requestLanguage: "bash",
    successResponse: `{
  "success": true,
  "url": "https://app.forgepdf.io/api/download/your-document.pdf",
  "error": ""
}`,
    errorResponse: `{
  "success": false,
  "url": "",
  "error": "Template not found"
}`,
  },
  "list-templates": {
    key: "list-templates",
    navigationLabel: "List templates",
    group: "Operation",
    icon: FileIcon,
    method: "GET",
    path: "/operations/templates",
    title: "List your templates",
    description: "Return metadata for every template owned by the API key.",
    result: "Use this to discover template IDs before retrieving a template or generating a document.",
    requestExample: `curl '${BASE_URL}/operations/templates' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
    requestLanguage: "bash",
    successResponse: `{
  "success": true,
  "data": {
    "templates": [{
      "id": "template_id",
      "name": "Invoice",
      "config": { "paper_size": "A4" },
      "created_at": "2026-07-10T10:00:00.000Z",
      "updated_at": "2026-07-10T10:00:00.000Z"
    }]
  }
}`,
    errorResponse: `{
  "success": false,
  "error": { "code": "unauthorized", "message": "A ForgePDF API key is required" }
}`,
  },
  "get-template": {
    key: "get-template",
    navigationLabel: "Get template details",
    group: "Operation",
    icon: FileIcon,
    method: "GET",
    path: "/operations/templates/:template_id",
    title: "Get template details",
    description: "Return the owned template HTML, PDF settings, sample data, and discovered variables.",
    result: "Use this to view or reuse a template without opening the ForgePDF dashboard.",
    requestExample: `curl '${BASE_URL}/operations/templates/your_template_id' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
    requestLanguage: "bash",
    successResponse: `{
  "success": true,
  "data": {
    "template": {
      "id": "template_id",
      "name": "Invoice",
      "html": "<!doctype html>...",
      "sample_data": { "customer": { "name": "Acme" } },
      "variables": ["customer", "customer.name"]
    }
  }
}`,
    errorResponse: `{
  "success": false,
  "error": { "code": "not_found", "message": "Template not found" }
}`,
  },
  "create-template": {
    key: "create-template",
    navigationLabel: "Create HTML template",
    group: "Operation",
    icon: PlusIcon,
    method: "POST",
    path: "/operations/templates",
    title: "Create an HTML template",
    description: "Create an owned HTML or Handlebars template directly through the API.",
    result: "Use this to build an API-only ForgePDF workflow without using the template editor.",
    contentTypeRequired: true,
    fields: [
      { name: "name", type: "string", required: true, description: "A readable name for the template." },
      { name: "html", type: "string", required: true, description: "Complete HTML with optional Handlebars variables." },
      { name: "config", type: "object", description: "Optional PDF settings. Defaults match the ForgePDF editor." },
      { name: "sample_data", type: "object", description: "Optional example values used to discover template variables." },
    ],
    requestExample: `curl -X POST \\
  '${BASE_URL}/operations/templates' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "name": "Invoice",
    "html": "<!doctype html><h1>Invoice {{invoice_number}}</h1>",
    "sample_data": { "invoice_number": "INV-001" }
  }'`,
    requestLanguage: "bash",
    successResponse: `{
  "success": true,
  "data": {
    "template": {
      "id": "new_template_id",
      "name": "Invoice",
      "variables": ["invoice_number"]
    }
  }
}`,
    errorResponse: `{
  "success": false,
  "error": { "code": "invalid_request", "message": "html is required" }
}`,
  },
  "account-overview": {
    key: "account-overview",
    navigationLabel: "Account overview",
    group: "Operation",
    icon: GraphIcon,
    method: "GET",
    path: "/operations/account/overview",
    title: "Get wallet and analytics overview",
    description: "Return wallet balances, account resource counts, and PDF request analytics in one call.",
    result: "Use this to show current available tokens and generation reliability without separate analytics requests.",
    fieldsLabel: "Query parameters",
    fields: [
      { name: "period_days", type: "7 | 30 | 90", description: "Analytics period. Defaults to 30 days." },
    ],
    requestExample: `curl '${BASE_URL}/operations/account/overview?period_days=30' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
    requestLanguage: "bash",
    successResponse: `{
  "success": true,
  "data": {
    "wallet": { "available_tokens": 250, "free_remaining": 980 },
    "resources": { "templates": 4, "api_keys": 1 },
    "analytics": { "period_days": 30, "requests_total": 24, "success_rate": 100 }
  }
}`,
    errorResponse: `{
  "success": false,
  "error": { "code": "invalid_request", "message": "period_days must be 7, 30, or 90" }
}`,
  },
  "connect-mcp": {
    key: "connect-mcp",
    navigationLabel: "Connect an MCP client",
    group: "MCP",
    icon: KeyIcon,
    method: "POST",
    path: "/mcp",
    title: "Connect ForgePDF to an AI chatbot",
    description: "Configure any Streamable HTTP MCP client with your ForgePDF API key. The server exposes template, wallet, analytics, PDF-file, and PDF-URL tools.",
    result: "Use this to let an AI assistant create templates and generate PDFs using ForgePDF tools.",
    requestLabel: "Codex configuration",
    requestLanguage: "toml",
    requestExample: `[mcp_servers.forgepdf]
url = "${MCP_URL}"
http_headers = { Authorization = "Bearer YOUR_API_KEY" }`,
    successResponse: `Available tools
- forgepdf_get_api_schema
- forgepdf_list_templates
- forgepdf_get_template
- forgepdf_create_template
- forgepdf_get_account_overview
- forgepdf_generate_pdf_file
- forgepdf_generate_pdf_url`,
    errorResponse: `# 401 Unauthorized
{
  "error": "Unauthorized"
}`,
    responseLanguage: "text",
  },
};

const groupOrder: EndpointDefinition["group"][] = ["Generation", "Operation", "MCP"];
const endpointGroups = groupOrder.map((group) => [
  group,
  Object.values(endpoints).filter((endpoint) => endpoint.group === group),
] as const);

function CodeBlock({ title, code, language }: { title: string; code: string; language: string }) {
  const [copied, setCopied] = useState(false);
  const highlightedCode = useMemo(
    () => hljs.highlight(code, { language, ignoreIllegals: true }).value,
    [code, language],
  );

  const copyCode = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section className="api-code-block" aria-label={title}>
      <header className="api-code-header">
        <span>{title}</span>
        <button type="button" onClick={copyCode} aria-label={`Copy ${title.toLowerCase()}`}>
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </header>
      <pre><code className={`language-${language} hljs`} dangerouslySetInnerHTML={{ __html: highlightedCode }} /></pre>
    </section>
  );
}

function McpConfigurationPanel() {
  const [selectedClient, setSelectedClient] = useState<McpClientKey>("generic");
  const configuration = mcpClientConfigurations[selectedClient];

  return (
    <>
      <div className="api-code-panel-heading api-mcp-panel-heading">
        <div><span>MCP client setup</span><strong>{configuration.title}</strong></div>
        <span className="api-live-badge">Streamable HTTP</span>
      </div>
      <div className="api-mcp-client-tabs" role="tablist" aria-label="MCP client configurations">
        {Object.values(mcpClientConfigurations).map((client) => (
          <button
            key={client.key}
            type="button"
            role="tab"
            aria-selected={selectedClient === client.key}
            className={selectedClient === client.key ? "is-active" : ""}
            onClick={() => setSelectedClient(client.key)}
          >
            {client.label}
          </button>
        ))}
      </div>
      <p className="api-mcp-configuration-description">{configuration.description}</p>
      <CodeBlock title={configuration.location} code={configuration.code} language={configuration.language} />
    </>
  );
}

function EndpointNavigation({ selected, onSelect }: { selected: EndpointKey; onSelect: (key: EndpointKey) => void }) {
  return (
    <nav className="api-endpoints-nav" aria-label="API endpoints">
      <div className="api-endpoints-nav-heading"><span>Endpoints</span><small>{Object.keys(endpoints).length}</small></div>
      {endpointGroups.map(([group, groupEndpoints]) => (
        <section className="api-endpoints-group" key={group} aria-label={group}>
          <h2>{group}</h2>
          <div className="api-endpoints-list">
            {groupEndpoints.map((endpoint) => {
              const Icon = endpoint.icon;
              const active = selected === endpoint.key;
              return (
                <button key={endpoint.key} type="button" aria-current={active ? "page" : undefined} className={active ? "is-active" : ""} onClick={() => onSelect(endpoint.key)}>
                  <Icon size={15} />
                  <span><strong>{endpoint.navigationLabel}</strong><small><b>{endpoint.method}</b> {endpoint.path}</small></span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </nav>
  );
}

export default function ApiDocumentation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedResponse, setSelectedResponse] = useState<ResponseState>("success");
  const requestedSection = new URLSearchParams(location.search).get("section") as EndpointKey | null;
  const selectedKey = requestedSection && endpoints[requestedSection] ? requestedSection : "generate-pdf";
  const endpoint = endpoints[selectedKey];
  const isMcpSetup = selectedKey === "connect-mcp";

  useEffect(() => setSelectedResponse("success"), [selectedKey]);

  const selectEndpoint = (key: EndpointKey) => {
    const params = new URLSearchParams(location.search);
    params.set("section", key);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  return (
    <DashboardLayout>
      <div className="app-page api-docs-page">
        <header className="api-docs-intro">
          <span className="api-docs-eyebrow">API reference</span>
          <h1>Build with ForgePDF</h1>
          <p>Generate PDFs, manage HTML templates, inspect account usage, or connect ForgePDF directly to an AI chatbot.</p>
          <div className="api-base-url"><span>Base URL</span><code>{BASE_URL}</code></div>
        </header>

        <div className="api-docs-layout">
          <EndpointNavigation selected={selectedKey} onSelect={selectEndpoint} />
          <article className="api-docs-copy">
            <div className="api-endpoint-heading"><span className="api-method-badge">{endpoint.method}</span><code>{endpoint.path}</code></div>
            <h2>{endpoint.title}</h2>
            <p className="api-endpoint-description">{endpoint.description}</p>
            <div className="api-outcome-card"><CheckCircleIcon size={18} /><div><strong>When to use this endpoint</strong><span>{endpoint.result}</span></div></div>

            <section className="api-reference-section">
              <h3>Authentication</h3>
              <div className="api-auth-card"><span className="api-reference-icon"><LockIcon size={16} /></span><div><strong>Bearer authentication</strong><span>Send your ForgePDF API key in the Authorization header.</span></div><code>Bearer YOUR_API_KEY</code></div>
            </section>

            <section className="api-reference-section">
              <h3>Headers</h3>
              <div className="api-field-list">
                <div className="api-field-row"><div><code>Authorization</code><span className="api-required">Required</span></div><p>Your API key using the Bearer scheme.</p></div>
                {endpoint.contentTypeRequired && <div className="api-field-row"><div><code>Content-Type</code><span className="api-required">Required</span></div><p><code>application/json</code></p></div>}
              </div>
            </section>

            {endpoint.fields?.length ? <section className="api-reference-section">
              <h3>{endpoint.fieldsLabel || "Request body"}</h3>
              <div className="api-field-list">
                {endpoint.fields.map((field) => <div className="api-field-row" key={field.name}><div><code>{field.name}</code><span className="api-type">{field.type}</span>{field.required && <span className="api-required">Required</span>}</div><p>{field.description}</p></div>)}
              </div>
            </section> : null}

            <aside className="api-prerequisite-note"><span className="api-reference-icon"><KeyIcon size={16} /></span><div><strong>Before you send a request</strong><span>Create a ForgePDF API key. API-only workflows can create and retrieve templates without using the dashboard.</span></div></aside>
          </article>

          <aside className="api-docs-code-panel">
            {isMcpSetup ? <McpConfigurationPanel /> : <><div className="api-code-panel-heading"><div><span>Example request</span><strong>{endpoint.requestLabel || "cURL"}</strong></div><span className="api-live-badge">Production</span></div><CodeBlock title="Request" code={endpoint.requestExample} language={endpoint.requestLanguage} /></>}
            <div className="api-response-heading"><h3>Response</h3><div className="api-response-tabs" role="tablist" aria-label="Response examples"><button type="button" role="tab" aria-selected={selectedResponse === "success"} className={selectedResponse === "success" ? "is-active" : ""} onClick={() => setSelectedResponse("success")}>Success</button><button type="button" role="tab" aria-selected={selectedResponse === "error"} className={selectedResponse === "error" ? "is-active is-error" : ""} onClick={() => setSelectedResponse("error")}>Error</button></div></div>
            <CodeBlock title={selectedResponse === "success" ? "200 response" : "Error response"} code={selectedResponse === "success" ? endpoint.successResponse : endpoint.errorResponse} language={selectedResponse === "success" ? endpoint.responseLanguage || "json" : "json"} />
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
